/**
 * Unified Tier Mapping System - Single Source of Truth
 * 
 * Standard tiers: "demo" | "starter" | "core_plus"
 * Legacy mappings: home_chef → starter, master_chef → core_plus
 */

import type { SubscriptionTier } from '../types/subscription'
import {
  DISPLAY_NAME,
  normalizeTier as normalizeCoreTier,
  isAIEnabled,
  TIER_ORDER,
  compareTiers,
} from '../billing/tiers'

// Legacy tier values that need to be mapped
export type LegacyTier = 'kitchen_apprentice' | 'home_chef' | 'master_chef'

// All possible tier values in the system
export type AllTierValues = SubscriptionTier | LegacyTier

// Compatibility alias for older imports
export type BackendTier = AllTierValues

/**
 * Normalize any tier string to standard SubscriptionTier
 */
export function normalizeTier(tier: string | undefined | null): SubscriptionTier {
  const normalized = normalizeCoreTier(tier || 'demo')
  // Legacy aliases handled in billing/tiers; add legacy-only ones here if needed
  if (!tier) return normalized
  const lowerTier = tier.toLowerCase()
  if (lowerTier === 'family') return 'starter'
  if (lowerTier === 'premium' || lowerTier === 'pro') return 'core_plus'
  return normalized
}

/**
 * Convert standard tier to legacy tier for backward compatibility
 */
export function toLegacyTier(tier: SubscriptionTier): LegacyTier {
  switch (tier) {
    case 'starter':
      return 'home_chef'
    case 'core_plus':
      return 'master_chef'
    default:
      return 'home_chef' // demo maps to home_chef for legacy systems
  }
}

/**
 * Get display name for tier
 */
export function getTierDisplayName(tier: SubscriptionTier): string {
  return DISPLAY_NAME[normalizeTier(tier)] || 'Demo'
}

/**
 * Get legacy display name (for systems still using old tier names)
 */
export function getLegacyTierDisplayName(tier: string): string {
  const normalized = normalizeTier(tier);
  return getTierDisplayName(normalized);
}

/**
 * Check if tier has premium features
 */
export function isPremiumTier(tier: SubscriptionTier): boolean {
  return isAIEnabled(normalizeTier(tier))
}

/**
 * Check if tier has family features
 */
export function isFamilyTier(tier: SubscriptionTier): boolean {
  const normalized = normalizeTier(tier)
  return normalized === 'starter' || normalized === 'core_plus'
}

/**
 * Get tier hierarchy order (higher number = more premium)
 */
export function getTierOrder(tier: SubscriptionTier): number {
  return TIER_ORDER[normalizeTier(tier)] ?? 0
}

/**
 * Compare tiers (returns true if tier1 >= tier2)
 */
export function tierIsAtLeast(tier1: SubscriptionTier, tier2: SubscriptionTier): boolean {
  return compareTiers(normalizeTier(tier1), normalizeTier(tier2)) >= 0
}

/**
 * Get usage limits by tier
 */
export function getUsageLimits(tier: SubscriptionTier): { plans: number; swaps: number } {
  const normalized = normalizeTier(tier)
  switch (normalized) {
    case 'demo':
      return { plans: 1, swaps: 1 }
    case 'starter':
      return { plans: 15, swaps: 30 }
    case 'core_plus':
      return { plans: Infinity, swaps: Infinity }
    default:
      return { plans: 1, swaps: 1 }
  }
}

// Legacy compatibility exports
export const mapTierToUI = normalizeTier;
export const mapBackendToUi = normalizeTier;
export type UITier = SubscriptionTier;
export type Tier = AllTierValues;
// Re-export canonical SubscriptionTier for convenience in legacy imports
export type { SubscriptionTier };
